/**
 * Leitura da NFS-e do padrão nacional (Sistema Nacional NFS-e, leiaute SEFIN/ADN 1.01 — Anexo I e XSD de 09/02/2026)
 * para o confronto. Aceita a NFS-e solta (<NFSe><infNFSe>…<DPS>…</DPS></infNFSe></NFSe>) ou várias num mesmo arquivo.
 *
 * A NFS-e vira uma NotaLida de modelo 'NFSE' com UM item — o serviço —, para usar o mesmo motor, a mesma gravação e as
 * mesmas telas da NF-e: `emit` é o prestador, `dest` o tomador, e o item leva os grupos issqn/pis/cofins/ibscbs
 * preenchidos a partir da nota. O que só existe na NFS-e fica em `nota.servico` (tipos.ts, ServicoLido).
 *
 * DPS sem NFS-e (ainda não gerada) não tem chave de acesso e é rejeitada.
 */
import { ArquivoInvalido } from './nfe';
import type { EnderecoLido, EventoNfseLido, ItemLido, NotaLida, PessoaServicoLida, ServicoLido } from './tipos';
import { MODELO_NFSE } from './tipos';
import { buscar, filho, lerXml, valor, type NoXml } from './xml';

const numero = (s: string | null): number | null => {
  if (s === null) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

/** UF pelo código IBGE do município (os dois primeiros dígitos). */
const UF_IBGE: Record<string, string> = {
  '11': 'RO', '12': 'AC', '13': 'AM', '14': 'RR', '15': 'PA', '16': 'AP', '17': 'TO',
  '21': 'MA', '22': 'PI', '23': 'CE', '24': 'RN', '25': 'PB', '26': 'PE', '27': 'AL', '28': 'SE', '29': 'BA',
  '31': 'MG', '32': 'ES', '33': 'RJ', '35': 'SP', '41': 'PR', '42': 'SC', '43': 'RS',
  '50': 'MS', '51': 'MT', '52': 'GO', '53': 'DF',
};
export const ufDoMunicipio = (cMun: string | null): string | null => (cMun && /^\d{7}$/.test(cMun) ? (UF_IBGE[cMun.slice(0, 2)] ?? null) : null);

/** Código de Tributação Nacional (6 dígitos) → item da LC 116 no formato da NF-e conjugada (cListServ "14.01"). */
export const itemLc116 = (cTribNac: string | null): string | null => (cTribNac && /^\d{6}$/.test(cTribNac) ? `${cTribNac.slice(0, 2)}.${cTribNac.slice(2, 4)}` : null);

const documento = (no: NoXml | null) => valor(no, 'CNPJ') ?? valor(no, 'CPF') ?? valor(no, 'NIF');

/** Tomador/intermediário (TCInfoPessoa): o município vem de end/endNac/cMun (exterior: sem município). */
function lerPessoa(no: NoXml | null): PessoaServicoLida | null {
  if (!no) return null;
  return { doc: documento(no), nome: valor(no, 'xNome'), cMun: valor(filho(filho(no, 'end'), 'endNac'), 'cMun'), im: valor(no, 'IM') };
}

/** Endereço do emitente (TCEmitente/enderNac) no formato do DANFE. */
function enderecoEmitente(no: NoXml | null): EnderecoLido | null {
  if (!no) return null;
  return {
    xLgr: valor(no, 'xLgr'),
    nro: valor(no, 'nro'),
    xCpl: valor(no, 'xCpl'),
    xBairro: valor(no, 'xBairro'),
    cMun: valor(no, 'cMun'),
    xMun: null,
    uf: valor(no, 'UF'),
    cep: valor(no, 'CEP'),
    fone: null,
  };
}

/** Endereço do tomador (TCEndereco: logradouro no próprio grupo, município e CEP em endNac). */
function enderecoTomador(no: NoXml | null): EnderecoLido | null {
  const end = filho(no, 'end');
  if (!end) return null;
  const nac = filho(end, 'endNac');
  const cMun = valor(nac, 'cMun');
  return {
    xLgr: valor(end, 'xLgr'),
    nro: valor(end, 'nro'),
    xCpl: valor(end, 'xCpl'),
    xBairro: valor(end, 'xBairro'),
    cMun,
    xMun: null,
    uf: ufDoMunicipio(cMun),
    cep: valor(nac, 'CEP'),
    fone: valor(no, 'fone'),
  };
}

function lerNfse(nfse: NoXml, arquivo: string): NotaLida {
  const inf = filho(nfse, 'infNFSe');
  if (!inf) throw new ArquivoInvalido('NFS-e sem o grupo infNFSe');
  const chave = (inf.attrs.Id ?? '').replace(/^NFS/, '');
  if (!/^\d{50}$/.test(chave)) throw new ArquivoInvalido('NFS-e sem chave de acesso válida (atributo Id do infNFSe, 50 dígitos)');
  const dps = filho(filho(inf, 'DPS'), 'infDPS');
  if (!dps) throw new ArquivoInvalido('NFS-e sem a DPS (infNFSe/DPS/infDPS)');

  const emit = filho(inf, 'emit');
  const enderEmit = filho(emit, 'enderNac');
  const valoresNfse = filho(inf, 'valores');
  const prest = filho(dps, 'prest');
  const regTrib = filho(prest, 'regTrib');
  const toma = filho(dps, 'toma');
  const serv = filho(dps, 'serv');
  const locPrest = filho(serv, 'locPrest');
  const cServ = filho(serv, 'cServ');
  const valoresDps = filho(dps, 'valores');
  const vServPrest = filho(valoresDps, 'vServPrest');
  const desconto = filho(valoresDps, 'vDescCondIncond');
  const dedRed = filho(valoresDps, 'vDedRed');
  const trib = filho(valoresDps, 'trib');
  const tribMun = filho(trib, 'tribMun');
  const bm = filho(tribMun, 'BM');
  const tribFed = filho(trib, 'tribFed');
  const piscofins = filho(tribFed, 'piscofins');
  const ibsDps = filho(dps, 'IBSCBS');
  const gIBSCBS = filho(filho(filho(ibsDps, 'valores'), 'trib'), 'gIBSCBS');
  const ibsNfse = filho(inf, 'IBSCBS');
  const ibsValores = filho(ibsNfse, 'valores');
  const ufV = filho(ibsValores, 'uf');
  const munV = filho(ibsValores, 'mun');
  const fedV = filho(ibsValores, 'fed');
  const totCIBS = filho(ibsNfse, 'totCIBS');
  const gIBS = filho(totCIBS, 'gIBS');

  const cTribNac = valor(cServ, 'cTribNac');
  const tomador = lerPessoa(toma);
  const servico: ServicoLido = {
    cTribNac,
    xTribNac: valor(inf, 'xTribNac'),
    cTribMun: valor(cServ, 'cTribMun'),
    xTribMun: valor(inf, 'xTribMun'),
    cNBS: valor(cServ, 'cNBS'),
    xNBS: valor(inf, 'xNBS'),
    xDescServ: valor(cServ, 'xDescServ'),
    cLocPrestacao: valor(locPrest, 'cLocPrestacao'),
    cPaisPrestacao: valor(locPrest, 'cPaisPrestacao'),
    cLocIncid: valor(inf, 'cLocIncid'),
    xLocIncid: valor(inf, 'xLocIncid'),
    dCompet: valor(dps, 'dCompet'),
    serieDps: valor(dps, 'serie'),
    nDps: valor(dps, 'nDPS'),
    cStat: valor(inf, 'cStat'),
    dhProc: valor(inf, 'dhProc'),
    tpEmit: valor(dps, 'tpEmit'),
    chSubstda: valor(filho(dps, 'subst'), 'chSubstda'),
    prestador: { im: valor(prest, 'IM') ?? (['1', null].includes(valor(dps, 'tpEmit')) ? valor(emit, 'IM') : null), opSimpNac: valor(regTrib, 'opSimpNac'), regApTribSN: valor(regTrib, 'regApTribSN'), regEspTrib: valor(regTrib, 'regEspTrib') },
    tomador,
    intermediario: lerPessoa(filho(dps, 'interm')),
    vServ: numero(valor(vServPrest, 'vServ')),
    vDescIncond: numero(valor(desconto, 'vDescIncond')),
    vDescCond: numero(valor(desconto, 'vDescCond')),
    pDR: numero(valor(dedRed, 'pDR')),
    vDR: numero(valor(dedRed, 'vDR')),
    vCalcDR: numero(valor(valoresNfse, 'vCalcDR')),
    nBM: valor(bm, 'nBM'),
    tpBM: valor(valoresNfse, 'tpBM'),
    vRedBCBM: numero(valor(bm, 'vRedBCBM')),
    vCalcBM: numero(valor(valoresNfse, 'vCalcBM')),
    tribISSQN: valor(tribMun, 'tribISSQN'),
    tpImunidade: valor(tribMun, 'tpImunidade'),
    tpSusp: valor(filho(tribMun, 'exigSusp'), 'tpSusp'),
    tpRetISSQN: valor(tribMun, 'tpRetISSQN'),
    pAliq: numero(valor(tribMun, 'pAliq')),
    pAliqAplic: numero(valor(valoresNfse, 'pAliqAplic')),
    vBC: numero(valor(valoresNfse, 'vBC')),
    vISSQN: numero(valor(valoresNfse, 'vISSQN')),
    vTotalRet: numero(valor(valoresNfse, 'vTotalRet')),
    vLiq: numero(valor(valoresNfse, 'vLiq')),
    tpRetPisCofins: valor(piscofins, 'tpRetPisCofins'),
    vRetCP: numero(valor(tribFed, 'vRetCP')),
    vRetIRRF: numero(valor(tribFed, 'vRetIRRF')),
    vRetCSLL: numero(valor(tribFed, 'vRetCSLL')),
    ibscbs: ibsDps || ibsNfse
      ? {
          cIndOp: valor(ibsDps, 'cIndOp'),
          finNFSe: valor(ibsDps, 'finNFSe'),
          indFinal: valor(ibsDps, 'indFinal'),
          indDest: valor(ibsDps, 'indDest'),
          cLocalidadeIncid: valor(ibsNfse, 'cLocalidadeIncid'),
          vBC: numero(valor(ibsValores, 'vBC')),
          vCalcReeRepRes: numero(valor(ibsValores, 'vCalcReeRepRes')),
          pAliqEfetUF: numero(valor(ufV, 'pAliqEfetUF')),
          pAliqEfetMun: numero(valor(munV, 'pAliqEfetMun')),
          pAliqEfetCBS: numero(valor(fedV, 'pAliqEfetCBS')),
          vIBSTot: numero(valor(gIBS, 'vIBSTot')),
          vTotNF: numero(valor(totCIBS, 'vTotNF')),
        }
      : null,
  };

  // O item único: os grupos comuns da NF-e preenchidos a partir da NFS-e, para as regras e a gravação que já existem.
  const item: ItemLido = {
    nItem: 1,
    cProd: cTribNac,
    xProd: servico.xDescServ ?? servico.xTribNac,
    ncm: null,
    cest: null,
    cBenef: null,
    cfop: null,
    vProd: servico.vServ,
    indTot: true,
    qCom: null,
    uCom: null,
    vUnCom: null,
    vDesc: servico.vDescIncond,
    vFrete: null,
    vSeg: null,
    vOutro: null,
    vII: null,
    icms: null,
    icmsUFDest: null,
    ipi: null,
    pis: piscofins ? { cst: valor(piscofins, 'CST'), vBC: numero(valor(piscofins, 'vBCPisCofins')), pAliquota: numero(valor(piscofins, 'pAliqPis')), vTributo: numero(valor(piscofins, 'vPis')) } : null,
    cofins: piscofins ? { cst: valor(piscofins, 'CST'), vBC: numero(valor(piscofins, 'vBCPisCofins')), pAliquota: numero(valor(piscofins, 'pAliqCofins')), vTributo: numero(valor(piscofins, 'vCofins')) } : null,
    ibscbs: gIBSCBS || ibsNfse
      ? {
          cst: valor(gIBSCBS, 'CST'),
          cClassTrib: valor(gIBSCBS, 'cClassTrib'),
          vBC: numero(valor(ibsValores, 'vBC')),
          pIBSUF: numero(valor(ufV, 'pIBSUF')),
          vIBSUF: numero(valor(filho(gIBS, 'gIBSUFTot'), 'vIBSUF')),
          pRedIBSUF: numero(valor(ufV, 'pRedAliqUF')),
          pIBSMun: numero(valor(munV, 'pIBSMun')),
          vIBSMun: numero(valor(filho(gIBS, 'gIBSMunTot'), 'vIBSMun')),
          pRedIBSMun: numero(valor(munV, 'pRedAliqMun')),
          pCBS: numero(valor(fedV, 'pCBS')),
          vCBS: numero(valor(filho(totCIBS, 'gCBS'), 'vCBS')),
          pRedCBS: numero(valor(fedV, 'pRedAliqCBS')),
        }
      : null,
    issqn: {
      vBC: servico.vBC,
      vAliq: servico.pAliqAplic ?? servico.pAliq,
      vISSQN: servico.vISSQN,
      cMunFG: servico.cLocIncid,
      cListServ: itemLc116(cTribNac),
      indISS: servico.tribISSQN,
    },
  };

  const dhEmissao = valor(dps, 'dhEmi');
  // `emit` da NFS-e é quem emitiu: o prestador (tpEmit 1) ou o tomador/intermediário (2/3). A nota sempre leva o
  // PRESTADOR em `emit` — se outro emitiu, o prestador vem da DPS (prest, com endereço em end/endNac).
  const emitidaPeloPrestador = servico.tpEmit === null || servico.tpEmit === '1';
  const enderPrest = emitidaPeloPrestador ? enderecoEmitente(enderEmit) : enderecoTomador(prest);
  const cMunEmit = enderPrest?.cMun ?? null;
  return {
    arquivo,
    chave,
    modelo: MODELO_NFSE,
    serie: servico.serieDps,
    numero: valor(inf, 'nNFSe'),
    dhEmissao,
    dataEmissao: dhEmissao?.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? servico.dCompet,
    natOp: servico.xTribNac,
    emit: {
      cnpj: documento(prest) ?? (emitidaPeloPrestador ? documento(emit) : null),
      nome: valor(prest, 'xNome') ?? (emitidaPeloPrestador ? valor(emit, 'xNome') : null),
      uf: (emitidaPeloPrestador ? valor(enderEmit, 'UF') : null) ?? ufDoMunicipio(cMunEmit),
      crt: null,
      ie: null,
      enderEmit: enderPrest,
    },
    dest: {
      doc: tomador?.doc ?? null,
      nome: tomador?.nome ?? null,
      uf: ufDoMunicipio(tomador?.cMun ?? null),
      indIEDest: null,
      ie: null,
      enderDest: enderecoTomador(toma),
    },
    // A NFS-e é sempre emitida na prestação: do lado do prestador é saída.
    tpNF: '1',
    finNFe: null,
    idDest: null,
    indFinal: servico.ibscbs?.indFinal ?? null,
    indPres: null,
    vNF: servico.vServ,
    totais: { vProd: null, vBC: null, vICMS: null, vIPI: null, vPIS: null, vCOFINS: null },
    totaisIssqn: null,
    itens: [item],
    transp: null,
    pag: [],
    protocolo: { nProt: valor(inf, 'nDFSe'), dhRecbto: servico.dhProc },
    infAdic: { infCpl: valor(filho(serv, 'infoCompl'), 'xInfComp') ?? valor(inf, 'xOutInf') },
    servico,
  };
}

/** O arquivo parece NFS-e do padrão nacional ou evento dela (sem ler a árvore inteira). */
export const pareceNfse = (conteudo: string) => /<(?:\w+:)?(?:infNFSe|infDPS|infPedReg)[\s>]/.test(conteudo);

/** Eventos que tiram a NFS-e de circulação: cancelamento e cancelamento por substituição (Anexo II). */
const EVENTOS_CANCELAMENTO = ['101101', '105102'];

/**
 * Eventos de cancelamento do arquivo (pedRegEvento/infPedReg, solto ou dentro do evento distribuído pelo ADN). Outros
 * eventos (manifestação, análise fiscal…) são ignorados.
 */
export function lerEventosNfse(conteudo: string): EventoNfseLido[] {
  if (!/<(?:\w+:)?infPedReg[\s>]/.test(conteudo)) return [];
  let raiz: NoXml;
  try {
    raiz = lerXml(conteudo);
  } catch (e) {
    throw new ArquivoInvalido(`XML inválido: ${e instanceof Error ? e.message : String(e)}`);
  }
  const saida: EventoNfseLido[] = [];
  for (const inf of buscar(raiz, 'infPedReg')) {
    const chave = valor(inf, 'chNFSe');
    if (!chave || !/^\d{50}$/.test(chave)) continue;
    for (const codigo of EVENTOS_CANCELAMENTO) {
      const ev = filho(inf, `e${codigo}`);
      if (ev) saida.push({ chave, codigo, descricao: valor(ev, 'xDesc'), motivo: valor(ev, 'xMotivo'), dhEvento: valor(inf, 'dhEvento') });
    }
  }
  return saida;
}

/** Blocos de evento de cancelamento do arquivo, pela chave da NFS-e (texto original, para guardar junto da nota). */
export function extrairEventosNfsePorChave(conteudo: string): Map<string, string[]> {
  const saida = new Map<string, string[]>();
  for (const bloco of conteudo.match(/<(?:\w+:)?pedRegEvento[\s>][\s\S]*?<\/(?:\w+:)?pedRegEvento>/g) ?? []) {
    if (!EVENTOS_CANCELAMENTO.some((c) => bloco.includes(`e${c}`))) continue;
    const chave = /<(?:\w+:)?chNFSe>(\d{50})</.exec(bloco)?.[1];
    if (chave) saida.set(chave, [...(saida.get(chave) ?? []), bloco]);
  }
  return saida;
}

/** Todas as NFS-e de um arquivo. */
export function lerArquivoNfse(conteudo: string, arquivo: string): NotaLida[] {
  let raiz: NoXml;
  try {
    raiz = lerXml(conteudo);
  } catch (e) {
    throw new ArquivoInvalido(`XML inválido: ${e instanceof Error ? e.message : String(e)}`);
  }
  const notas = buscar(raiz, 'NFSe').filter((n) => filho(n, 'infNFSe'));
  if (!notas.length) {
    if (buscar(raiz, 'DPS').length) throw new ArquivoInvalido('DPS sem NFS-e: a nota ainda não foi gerada pelo sistema emissor (sem chave de acesso)');
    // Arquivo só com evento (cancelamento): não é nota — quem lê o lote aplica o evento à nota (index.ts, lerArquivos).
    if (buscar(raiz, 'infPedReg').length) return [];
    throw new ArquivoInvalido('O arquivo não contém NFS-e (elemento NFSe)');
  }
  return notas.map((n) => lerNfse(n, arquivo));
}

/**
 * XML bruto de cada NFS-e do arquivo, pela chave de acesso (50 dígitos) — o texto original, sem reserializar, para guardar
 * e para o DANFSe.
 */
export function extrairXmlNfsePorChave(conteudo: string): Map<string, string> {
  const saida = new Map<string, string>();
  for (const bloco of conteudo.match(/<(?:\w+:)?NFSe[\s>][\s\S]*?<\/(?:\w+:)?NFSe>/g) ?? []) {
    const chave = /Id\s*=\s*"NFS(\d{50})"/.exec(bloco)?.[1];
    if (chave) saida.set(chave, bloco);
  }
  return saida;
}

